import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "./authApi";

/**
 * MembersPanel — coach-only overlay listing every account, newest first,
 * with membership status, Discord link and a toggle for complimentary access.
 */
const C = {
  bg: "rgba(4,7,10,0.82)", panel: "#0b1018", card: "#0f1620", border: "#1a2535",
  dim: "#8aabb8", faint: "#4a6070", bright: "#c8dae8", white: "#e8f0f8",
  accent: "#00d4ff", green: "#00e5a0", yellow: "#ffd060", red: "#ff3f50", purple: "#c084fc", discord: "#5865f2",
};

function fmt(ms, withTime) {
  if (!ms) return "—";
  const d = new Date(ms);
  return withTime
    ? d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function statusOf(m) {
  if (m.role === "coach") return { label: "Coach", color: C.purple };
  if (["active", "trialing"].includes(m.membershipStatus)) {
    return m.cancelAtPeriodEnd
      ? { label: `Pro · ends ${fmt(m.until)}`, color: C.yellow }
      : { label: `Pro · renews ${fmt(m.until)}`, color: C.green };
  }
  if (m.membershipStatus === "past_due") return { label: "Pro · payment failed", color: C.yellow };
  if (m.comp) return { label: "Free access", color: C.accent };
  if (["canceled", "unpaid", "incomplete_expired"].includes(m.membershipStatus)) return { label: "Ended", color: C.faint };
  return { label: "No membership", color: C.faint };
}

const FILTERS = [
  { v: "all", label: "All" },
  { v: "access", label: "With access" },
  { v: "none", label: "No access" },
];

export default function MembersPanel({ onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setError("");
    try {
      setData(await api.getMembers());
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggleComp = async (m) => {
    const give = !m.comp;
    if (!window.confirm(give ? `Give ${m.name} free access?` : `Remove free access for ${m.name}?`)) return;
    setBusyId(m.id);
    try {
      await api.setCompAccess(m.id, give);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const endMembership = async (m) => {
    if (!window.confirm(`End ${m.name}'s membership now? Their access stops immediately and the Stripe subscription is cancelled.`)) return;
    setBusyId(m.id);
    try {
      await api.endMembership(m.id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.members.filter((m) => {
      if (filter === "access" && !m.hasAccess) return false;
      if (filter === "none" && m.hasAccess) return false;
      return !q || m.name.toLowerCase().includes(q) || m.username.toLowerCase().includes(q);
    });
  }, [data, query, filter]);

  const th = { textAlign: "left", padding: "8px 10px", fontSize: 9, color: C.dim, letterSpacing: "0.1em", textTransform: "uppercase", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.panel };
  const td = { padding: "9px 10px", fontSize: 11, color: C.bright, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" };

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 2000, background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'Courier New', monospace" }}>
      <div style={{ width: "min(980px, 100%)", maxHeight: "86vh", display: "flex", flexDirection: "column", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, boxShadow: "0 16px 48px rgba(0,0,0,0.6)" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 13, color: C.white, letterSpacing: "0.12em", textTransform: "uppercase" }}>Members</div>
          <span style={{ flex: 1 }} />
          <button onClick={load} title="Refresh" style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.dim, borderRadius: 2, padding: "4px 10px", cursor: "pointer", fontFamily: "monospace", fontSize: 11 }}>↻ Refresh</button>
          <button onClick={onClose} title="Close" style={{ background: "transparent", border: "none", color: C.dim, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        {/* Counts */}
        {data && (
          <div style={{ display: "flex", gap: 10, padding: "14px 20px", flexWrap: "wrap" }}>
            {[
              ["Accounts", data.counts.total, C.white],
              ["Paying", data.counts.paying, C.green],
              ["Free access", data.counts.comp, C.accent],
              ["New this week", data.counts.newThisWeek, C.yellow],
            ].map(([label, n, color]) => (
              <div key={label} style={{ flex: "1 1 140px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, padding: "10px 12px" }}>
                <div style={{ fontSize: 9, color: C.dim, letterSpacing: "0.1em", textTransform: "uppercase" }}>{label}</div>
                <div style={{ fontSize: 22, color, marginTop: 4 }}>{n}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: "flex", gap: 8, padding: "0 20px 12px", flexWrap: "wrap", alignItems: "center" }}>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or username…"
            style={{ flex: "1 1 220px", background: C.card, border: "1px solid #243040", color: C.white, padding: "7px 10px", borderRadius: 2, fontFamily: "monospace", fontSize: 12 }} />
          {FILTERS.map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)}
              style={{ background: filter === f.v ? "rgba(0,212,255,0.1)" : "transparent", border: `1px solid ${filter === f.v ? "rgba(0,212,255,0.4)" : C.border}`, color: filter === f.v ? C.accent : C.dim, borderRadius: 2, padding: "6px 10px", cursor: "pointer", fontFamily: "monospace", fontSize: 11 }}>
              {f.label}
            </button>
          ))}
        </div>

        {error && <div style={{ margin: "0 20px 12px", fontSize: 11, color: C.red, padding: "6px 8px", background: "rgba(255,63,80,0.08)", border: "1px solid rgba(255,63,80,0.25)", borderRadius: 2 }}>{error}</div>}

        {/* Table */}
        <div style={{ overflow: "auto", flex: 1, padding: "0 20px 16px" }}>
          {!data && !error && <div style={{ color: C.dim, fontSize: 11, padding: 20 }}>Loading…</div>}
          {data && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Name</th>
                  <th style={th}>Username</th>
                  <th style={th}>Registered</th>
                  <th style={th}>Status</th>
                  <th style={th}>Discord</th>
                  <th style={{ ...th, textAlign: "right" }}>Free access</th>
                  <th style={{ ...th, textAlign: "right" }}>Membership</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const st = statusOf(m);
                  return (
                    <tr key={m.id}>
                      <td style={{ ...td, color: C.white }}>{m.name}</td>
                      <td style={td}>{m.username}</td>
                      <td style={{ ...td, color: C.dim }}>{fmt(m.createdAt, true)}</td>
                      <td style={{ ...td, color: st.color }}>{st.label}</td>
                      <td style={{ ...td, color: m.discordLinked ? C.discord : C.faint }}>{m.discordLinked ? "✓ linked" : "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {m.role === "coach" ? <span style={{ color: C.faint }}>—</span> : (
                          <button disabled={busyId === m.id} onClick={() => toggleComp(m)}
                            style={{ background: "transparent", border: `1px solid ${m.comp ? "rgba(255,63,80,0.4)" : "rgba(0,212,255,0.4)"}`, color: m.comp ? C.red : C.accent, borderRadius: 2, padding: "4px 10px", cursor: "pointer", fontFamily: "monospace", fontSize: 10, opacity: busyId === m.id ? 0.5 : 1 }}>
                            {m.comp ? "Remove" : "Give"}
                          </button>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {m.role !== "coach" && ["active", "trialing", "past_due"].includes(m.membershipStatus) ? (
                          <button disabled={busyId === m.id} onClick={() => endMembership(m)}
                            style={{ background: "transparent", border: "1px solid rgba(255,63,80,0.4)", color: C.red, borderRadius: 2, padding: "4px 10px", cursor: "pointer", fontFamily: "monospace", fontSize: 10, opacity: busyId === m.id ? 0.5 : 1 }}>
                            End now
                          </button>
                        ) : <span style={{ color: C.faint }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, color: C.dim, textAlign: "center", padding: 24 }}>No members found.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
